import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminService } from '../services/api';
import { useAdminModal } from './hooks/UseAdminModal';
import { useAdminPage } from './hooks/UseAdminPage';
import {
  generateMessage,
  getCategoryLabel,
  getPermissionsByCategory,
} from './utils/AdminPanel';

// Temporarily hidden from the administration panel. Removing a name from this
// set restores it in the permissions list and user permissions modal.
const HIDDEN_ADMIN_PANEL_PERMISSIONS = new Set([
  'installation:manage',
  'installation:settlement'
]);

const visiblePermissionNames = (permissionNames = []) =>
  permissionNames.filter(name => !HIDDEN_ADMIN_PANEL_PERMISSIONS.has(name));

const AdminPanel = () => {
  const { isAdmin, user } = useAuth();
  const navigate = useNavigate();

  const {
    users, setUsers,
    roles, setRoles,
    permissions, setPermissions,
    loading, setLoading,
    error, setError,
    success, setSuccess,
    activeTab, setActiveTab,
    editMode, setEditMode
  } = useAdminPage();
  const modal = useAdminModal({
    setError,
    setSuccess,
    setUsers,
    adminService,
    setEditMode
  });
  const visiblePermissions = permissions.filter(
    permission => !HIDDEN_ADMIN_PANEL_PERMISSIONS.has(permission.name)
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [usersData, rolesData, permissionsData] = await Promise.all([
        adminService.getAllUsers(),
        adminService.getAllRoles(),
        adminService.getAllPermissions(),
      ]);
      // TODO: add set ignore so the loadData func won't run on each setter
      setUsers(usersData);
      setRoles(rolesData);
      setPermissions(permissionsData);
      setError('');
      setLoading(false);
    } catch (err) {
      let errMsg;
      if (err.response?.status === 403) {
        errMsg = 'Access denied. Admin privileges required.';
        setTimeout(() => navigate('/home'), 2000);
      } else {
        errMsg = `Failed to load data. ${err.response?.data?.detail || ''}`;
      }
      setError(errMsg);
    }
  }, [setError, setLoading, setUsers, setRoles, setPermissions, navigate]);

  useEffect(() => {
    if (!isAdmin()) {
      navigate('/home');
      return;
    }
    loadData();

  }, [isAdmin, navigate, loadData]);

  // Group permissions by category

  if (loading) {
    return (
      <div className="container">
        <div className="header">
          <h2>🛡️ Admin Panel</h2>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="header">
        <h2>🛡️ Admin Panel</h2>
        <p>Manage users, roles, and permissions</p>
      </div>

      <div className="content">
        {generateMessage(error, 'error')}
        {generateMessage(success, 'success')}

        {/* Tab Navigation */}
        <div className="admin-tabs">
          <button 
            className={`admin-tab ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            👥 Users
          </button>
          <button 
            className={`admin-tab ${activeTab === 'roles' ? 'active' : ''}`}
            onClick={() => setActiveTab('roles')}
          >
            🎭 Roles
          </button>
          <button 
            className={`admin-tab ${activeTab === 'permissions' ? 'active' : ''}`}
            onClick={() => setActiveTab('permissions')}
          >
            🔑 Permissions
          </button>
        </div>

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="admin-section">
            <h3>User Management ({users.length} users)</h3>
            <p className="section-description">
              Assign roles and permissions to users. Roles provide organizational hierarchy, 
              while permissions control access to specific tools and modules.
            </p>
            <div className="admin-users-table">
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Department</th>
                    <th>Roles</th>
                    <th>Permissions</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id} className={u.id === user?.id ? 'current-user-row' : ''}>
                      <td>{u.id}</td>
                      <td>
                        {u.full_name}
                        {u.id === user?.id && <span className="you-badge">You</span>}
                      </td>
                      <td>{u.email}</td>
                      <td>{u.department}</td>
                      <td>
                        <div className="user-roles">
                          {u.roles?.length > 0 ? (
                            u.roles.map(role => (
                              <span key={role} className={`role-tag ${role}`}>{role}</span>
                            ))
                          ) : (
                            <span className="no-roles">No roles</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="user-permissions-count">
                          {visiblePermissionNames(u.permissions).length} permissions
                        </div>
                      </td>
                      <td className="actions-cell">
                        <button 
                          className="btn-edit-roles"
                          onClick={() => modal.handleEditRoles(u)}
                        >
                          Roles
                        </button>
                        <button 
                          className="btn-edit-permissions"
                          onClick={() => modal.handleEditPermissions(u)}
                        >
                          Permissions
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Roles Tab */}
        {activeTab === 'roles' && (
          <div className="admin-section">
            <h3>Available Roles</h3>
            <p className="section-description">
              Roles represent organizational hierarchy (admin, manager, employee, etc.). 
              They do NOT automatically grant tool access.
            </p>
            <div className="roles-grid">
              {roles.map(role => (
                <div key={role.id} className="role-card">
                  <div className="role-card-header">
                    <span className={`role-tag large ${role.name}`}>{role.name}</span>
                  </div>
                  <p className="role-card-description">
                    {role.name === 'client' ? 'Access to assigned tools only.' : role.description}
                  </p>
                  {visiblePermissionNames(role.permissions).length > 0 && (
                    <div className="role-card-permissions">
                      <strong>Inherent permissions:</strong>
                      <div className="permission-tags">
                        {visiblePermissionNames(role.permissions).map(p => (
                          <span key={p} className="permission-tag-small">{p}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Permissions Tab */}
        {activeTab === 'permissions' && (
          <div className="admin-section">
            <h3>Available Permissions</h3>
            <p className="section-description">
              Permissions control access to specific tools and modules. 
              Assign directly to users for fine-grained control.
            </p>
            {Object.entries(getPermissionsByCategory(visiblePermissions))
                .map(([category, perms]) => (
              <div key={category} className="permission-category">
                <h4>{getCategoryLabel(category)}</h4>
                <div className="permissions-grid">
                  {perms.map(perm => (
                    <div key={perm.id} className="permission-card">
                      <code className="permission-name">{perm.name}</code>
                      <p className="permission-description">{perm.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Edit Roles Modal */}
        {modal.editedUser && editMode === 'roles' && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h3>Edit Roles for {modal.editedUser.full_name}</h3>
              <p className="modal-subtitle">{modal.editedUser.email}</p>
              <p className="modal-info">
                Roles define organizational hierarchy. Use permissions for tool access.
              </p>
              
              {/* Quick action to remove all roles */}
              <div className="no-roles-action">
                <button 
                  className={`btn-no-roles ${modal.selectedRoles.length === 0 ? 'active' : ''}`}
                  onClick={() => modal.setSelectedRoles([])}
                >
                  🚫 No Roles (Block Access)
                </button>
                <span className="no-roles-hint">
                  User will see "Contact administrator" message
                </span>
              </div>

              <div className="roles-divider">
                <span>Or select roles:</span>
              </div>
              
              <div className="roles-checkbox-list">
                {roles.map(role => (
                  <label key={role.id} className="role-checkbox">
                    <input
                      type="checkbox"
                      checked={modal.selectedRoles.includes(role.name)}
                      onChange={() => modal.handleRoleToggle(role.name)}
                    />
                    <span className={`role-checkbox-name ${role.name}`}>{role.name}</span>
                    <span className="role-checkbox-desc">
                      {role.name === 'client' ? 'Access to assigned tools only.' : role.description}
                    </span>
                  </label>
                ))}
              </div>

              {generateMessage(error, 'error')}
              {generateMessage(success, 'success')}

              <div className="modal-actions">
                <button className="btn-primary" onClick={modal.handleSaveRoles}>Save Roles</button>
                <button className="btn-secondary" onClick={modal.handleCloseModal}>Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Permissions Modal */}
        {modal.editedUser && editMode === 'permissions' && (
          <div className="modal-overlay">
            <div className="modal-content modal-large">
              <h3>Edit Permissions for {modal.editedUser.full_name}</h3>
              <p className="modal-subtitle">{modal.editedUser.email}</p>
              <p className="modal-info">
                Select which tools and modules this user can access.
              </p>
              
              <div className="permissions-checkbox-container">
                {Object.entries(getPermissionsByCategory(visiblePermissions))
                    .map(([category, perms]) => (
                  <div key={category} className="permission-category-section">
                    <h4>{getCategoryLabel(category)}</h4>
                    <div className="permissions-checkbox-list">
                      {perms.map(perm => (
                        <label key={perm.id} className="permission-checkbox">
                          <input
                            type="checkbox"
                            checked={modal.selectedPermissions.includes(perm.name)}
                            onChange={() => modal.handlePermissionToggle(perm.name)}
                          />
                          <div className="permission-checkbox-content">
                            <code className="permission-checkbox-name">{perm.name}</code>
                            <span className="permission-checkbox-desc">{perm.description}</span>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {generateMessage(error, 'error')}
              {generateMessage(success, 'success')}

              <div className="modal-actions">
                <button className="btn-primary" onClick={modal.handleSavePermissions}>Save Permissions</button>
                <button className="btn-secondary" onClick={modal.handleCloseModal}>Cancel</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;
